import { useEffect, useRef, useState } from 'react';
import { pairInit, pairPoll, type PairInitResponse } from '../api/pairing';
import { useAuthStore } from '../auth/authStore';
import { config } from '../config';
import './PairingScreen.css';

type State =
  | { kind: 'loading' }
  | { kind: 'showing'; init: PairInitResponse; remaining: number }
  | { kind: 'error'; message: string };

export function PairingScreen() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const setSession = useAuthStore((s) => s.setSession);
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    let abort = false;
    const controller = new AbortController();
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;

    const startPairing = async () => {
      try {
        const init = await pairInit();
        if (abort || requestRef.current !== requestId) return;
        const expiresAt = Date.now() + init.expires_in * 1000;
        setState({ kind: 'showing', init, remaining: init.expires_in });

        countdownTimer = setInterval(() => {
          if (abort) return;
          const remaining = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
          setState((prev) => (prev.kind === 'showing' ? { ...prev, remaining } : prev));
        }, 1000);

        const poll = async () => {
          if (abort) return;
          try {
            const result = await pairPoll(init.pairing_id, controller.signal);
            if (abort) return;
            if (result.status === 'approved') {
              setSession(result.access_token, result.refresh_token, result.user);
              return;
            }
            if (result.status === 'expired') {
              // Restart the whole flow with a new code
              if (countdownTimer) clearInterval(countdownTimer);
              setState({ kind: 'loading' });
              await startPairing();
              return;
            }
            pollTimer = setTimeout(poll, config.pairPollIntervalMs);
          } catch (err) {
            if (abort) return;
            if (err instanceof DOMException && err.name === 'AbortError') return;
            pollTimer = setTimeout(poll, config.pairPollIntervalMs * 2);
          }
        };

        pollTimer = setTimeout(poll, config.pairPollIntervalMs);
      } catch (err) {
        if (abort) return;
        const message = err instanceof Error ? err.message : 'Could not reach server';
        setState({ kind: 'error', message });
      }
    };

    void startPairing();

    return () => {
      abort = true;
      controller.abort();
      if (pollTimer) clearTimeout(pollTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [setSession]);

  return (
    <div className="pairing">
      <div className="pairing-header">EXPLORED · CABIN</div>

      <div className="pairing-body">
        {state.kind === 'loading' && <div className="pairing-loading">Generating code…</div>}

        {state.kind === 'error' && (
          <div className="pairing-error">
            <div className="pairing-error-title">Could not reach server</div>
            <div className="pairing-error-detail">{state.message}</div>
            <button className="pairing-retry" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}

        {state.kind === 'showing' && (
          <>
            <div className="pairing-code">
              {state.init.pair_code.split('').map((d, i) => (
                <span key={i} className="pairing-digit">
                  {d}
                </span>
              ))}
            </div>
            <div className="pairing-instructions">
              Open Explored on your phone, then tap{' '}
              <span className="pairing-emphasis">Profile → Pair a Cabin</span> and enter this code.
            </div>
            <div className="pairing-countdown">
              {state.remaining > 0
                ? `Expires in ${formatRemaining(state.remaining)}`
                : 'Refreshing…'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
